import bbox from '@turf/bbox';
import type { DataTableSelectionState } from 'components/data-table/types';
import type { AreasQueryResponse } from 'types/api-responses';
import type { SelectedArea } from 'components/create-area-panel/create-area-types';
import type { AreaSelectionConfig, DatasetSearchParams } from '../types';
import type { DatasetAreaSelection } from './types';

type Row = Record<string, unknown>;

/** Nothing checked, the table's empty selection. */
export const EMPTY_TABLE_SELECTION: DataTableSelectionState = {
  selectAll: false,
  toggledNodes: [],
};

/**
 * A row as the panel reads it: the id the backend takes, and a name to show.
 *
 * @param {Row} row - A results row.
 * @param {AreaSelectionConfig} config - The dataset's selection fields.
 * @return {SelectedArea} The row's id and name.
 */
export const toSelectedArea = (
  row: Row,
  config: AreaSelectionConfig
): SelectedArea => {
  const raw = row[config.idKey];
  const id =
    typeof raw === 'string' || typeof raw === 'number' ? String(raw) : '';
  const name = row[config.nameKey];
  return { id, name: typeof name === 'string' && name !== '' ? name : id };
};

/** What {@link nextAreaSelection} works from. */
export interface NextAreaSelectionInput {
  previous: DatasetAreaSelection | undefined;
  selection: DataTableSelectionState;
  pageRows: Row[];
  rowKey: (row: Row) => string;
  config: AreaSelectionConfig;
}

/**
 * The checked rows after a change in the table. A row checked on another page
 * keeps the name it had when it was checked, since only this page is loaded.
 * Select All, on a server-paged table, means the rows on screen.
 *
 * @param {NextAreaSelectionInput} input - The rows before the change, the table's
 * new selection, the rows on screen, the table's row id and the dataset's fields.
 * @return {DatasetAreaSelection} The new selection.
 */
export const nextAreaSelection = ({
  previous,
  selection,
  pageRows,
  rowKey,
  config,
}: NextAreaSelectionInput): DatasetAreaSelection => {
  const toggled = new Set(selection.toggledNodes.map(String));

  // A row without the id the backend takes can't seed an area, so it is left
  // out rather than sent as an id of 0.
  const buildable = pageRows.filter(
    (row) => toSelectedArea(row, config).id !== ''
  );

  if (selection.selectAll) {
    const onScreen = buildable.filter(
      (row) => !toggled.has(String(rowKey(row)))
    );
    return {
      selection,
      rows: onScreen.map((row) => toSelectedArea(row, config)),
      keys: onScreen.map((row) => String(rowKey(row))),
    };
  }

  // Keyed by the table's row id, which is not always the id the backend takes
  // (`config.idKey` names that one).
  const known = new Map<string, SelectedArea>();
  previous?.keys.forEach((key, index) => known.set(key, previous.rows[index]));
  buildable.forEach((row) =>
    known.set(String(rowKey(row)), toSelectedArea(row, config))
  );

  const entries = selection.toggledNodes.flatMap((node) => {
    const key = String(node);
    const row = known.get(key);
    return row ? [{ key, row }] : [];
  });
  return {
    selection,
    rows: entries.map((entry) => entry.row),
    keys: entries.map((entry) => entry.key),
  };
};

/**
 * The Areas search with its shared-only filter dropped, unless the new area is
 * shared and would be listed anyway.
 *
 * @param {DatasetSearchParams | undefined} current - The search as it stands.
 * @param {boolean} shared - Whether the new area is shared.
 * @return {DatasetSearchParams | undefined} The search to re-run.
 */
export const withoutSharedOnly = (
  current: DatasetSearchParams | undefined,
  shared: boolean
): DatasetSearchParams | undefined => {
  if (shared || current?.shared === undefined) return current;
  const { shared: _sharedOnly, ...rest } = current;
  return rest;
};

/**
 * Whether a record reads as an Areas row: an id and a name, as the table keys on.
 *
 * @param {Row} value - The record.
 * @return {boolean} True for an area.
 */
const isAreaRow = (value: Row): value is Row & AreasQueryResponse =>
  typeof value.id === 'number' && typeof value.name === 'string';

/**
 * The Areas row for an area just created, from its create response: the same
 * fields a search row carries (both render the area partial), with the corners
 * the search adds read off the returned geometry instead.
 *
 * @param {Row} created - The create response.
 * @return {(Row & AreasQueryResponse) | null} The row, or null when the response isn't an area.
 */
export const createdAreaRow = (
  created: Row
): (Row & AreasQueryResponse) | null => {
  const { geometry, ...row } = created;
  let boundingBox = null;
  if (geometry && typeof geometry === 'object') {
    const [swLng, swLat, neLng, neLat] = bbox(geometry);
    boundingBox = { swLng, swLat, neLng, neLat };
  }
  const listed = { ...row, boundingBox };
  return isAreaRow(listed) ? listed : null;
};
